import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MIN_PHOTOS = 2;
const MAX_PHOTOS = 4;
const ACCEPT_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const MAX_BYTES = 15 * 1024 * 1024;

function classifyExt(name) {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot).toLowerCase();
}

function validateFile(f) {
  const ext = classifyExt(f.name);
  if (!ACCEPT_EXTS.includes(ext)) return `'${f.name}' isn't a supported image type.`;
  if (f.size === 0) return `'${f.name}' looks empty.`;
  if (f.size > MAX_BYTES) return `'${f.name}' is larger than 15 MB.`;
  return "";
}

function joinNames(names) {
  if (!names || names.length === 0) return "your pet";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
}

export default function PhotoUploadPage() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [linkState, setLinkState] = useState({ status: "loading" });
  // filesByReport: { [report_id]: File[] }
  const [filesByReport, setFilesByReport] = useState({});
  // fileError: string on the pet section, keyed by report_id
  const [petErrors, setPetErrors] = useState({});
  const [ackTerms, setAckTerms] = useState(false);
  const [ackRights, setAckRights] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Fetch link info
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API_BASE}/api/public/upload/${encodeURIComponent(orderNumber)}?token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setLinkState({ status: "error", code: res.status, message: data.detail || "" });
        } else {
          setLinkState({ status: "ready", info: data });
          // Initialize filesByReport with each report id
          const init = {};
          (data.reports || []).forEach((r) => { init[r.report_id] = []; });
          setFilesByReport(init);
        }
      } catch {
        if (!cancelled) setLinkState({ status: "error", message: "Unable to reach the server." });
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber, token]);

  const info = linkState.info;
  const petNames = useMemo(() => (info?.reports || []).map((r) => r.pet_name), [info]);
  const heading = useMemo(() => `Great! Let's Meet ${joinNames(petNames)}!`, [petNames]);

  const setPetFiles = (reportId, files) => {
    setFilesByReport((prev) => ({ ...prev, [reportId]: files }));
    // Per-pet validation for size/type
    const err = files.map(validateFile).find(Boolean) || "";
    setPetErrors((prev) => ({ ...prev, [reportId]: err }));
    setSubmitError("");
  };

  const onFilesPicked = (reportId, e) => {
    const picked = Array.from(e.target.files || []);
    setPetFiles(reportId, picked);
  };

  const removeAt = (reportId, idx) => {
    setPetFiles(
      reportId,
      (filesByReport[reportId] || []).filter((_, i) => i !== idx),
    );
  };

  const allPetsValid = useMemo(() => {
    if (!info) return false;
    return (info.reports || []).every((r) => {
      const list = filesByReport[r.report_id] || [];
      if (list.length < MIN_PHOTOS || list.length > MAX_PHOTOS) return false;
      if (list.some((f) => validateFile(f))) return false;
      return true;
    });
  }, [info, filesByReport]);

  const canSubmit = allPetsValid && ackTerms && ackRights && !submitting && !done;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("ack_photo_terms", "true");
      fd.append("ack_photo_rights", "true");
      for (const r of info.reports) {
        const list = filesByReport[r.report_id] || [];
        for (const f of list) {
          fd.append(`files_report_${r.report_id}`, f, f.name);
        }
      }
      const url = `${API_BASE}/api/public/upload/${encodeURIComponent(orderNumber)}?token=${encodeURIComponent(token)}`;
      const res = await fetch(url, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.detail || "Upload failed. Please try again.");
      } else {
        setDone(true);
      }
    } catch {
      setSubmitError("Upload failed. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Rendering ----
  return (
    <div
      data-testid="photo-upload-page"
      className="min-h-screen bg-[#fdf7ef] text-neutral-900"
      style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}
    >
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-10">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-800/80">Paw-Some Reports</p>
          <h1 data-testid="upload-page-title" className="mt-2 text-2xl font-semibold sm:text-3xl">
            {linkState.status === "ready" ? heading : "Paw-Some Photo Upload"}
          </h1>
          <p className="mt-1 text-sm text-neutral-600" data-testid="upload-order-number">
            Order #{orderNumber}
          </p>
        </header>

        {linkState.status === "loading" && (
          <div data-testid="upload-loading" className="rounded-lg bg-white p-6 shadow-sm">
            Loading your upload link…
          </div>
        )}

        {linkState.status === "error" && (
          <div
            data-testid="upload-link-error"
            className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-900"
          >
            <p className="font-medium">This photo-upload link is no longer active.</p>
            <p className="mt-2 text-sm">
              If you've already submitted your photos, no further action is needed. For help,
              please contact Paw-Some Reports.
            </p>
          </div>
        )}

        {linkState.status === "ready" && done && (
          <SuccessBlock petNames={petNames} />
        )}

        {linkState.status === "ready" && !done && (
          <>
            {/* Sub-heading */}
            {info.reports?.length === 1 ? (
              <p className="mb-4 text-neutral-700" data-testid="upload-intro-copy">
                We're excited to get started on <strong>{petNames[0]}</strong>'s Paw-Some Report.
                Upload 2 to 4 photos. Paw-Some Reports will choose the photo that works best in
                the final report.
              </p>
            ) : (
              <p className="mb-4 text-neutral-700" data-testid="upload-intro-copy">
                We're excited to get started on each pet's Paw-Some Report. Upload
                2 to 4 photos <strong>per pet</strong>. Paw-Some Reports will choose the photo
                that works best in each final report.
              </p>
            )}

            {/* Per-pet sections */}
            {(info.reports || []).map((r) => (
              <PetSection
                key={r.report_id}
                report={r}
                files={filesByReport[r.report_id] || []}
                error={petErrors[r.report_id] || ""}
                onPick={(e) => onFilesPicked(r.report_id, e)}
                onRemove={(idx) => removeAt(r.report_id, idx)}
              />
            ))}

            {/* Photo tips */}
            <MissPawSomeTips />

            {/* Acknowledgments */}
            <section
              data-testid="acknowledgments-section"
              className="mt-6 rounded-lg border border-neutral-200 bg-white p-4 sm:p-5"
            >
              <label className="flex items-start gap-3 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  data-testid="ack-photo-terms-checkbox"
                  checked={ackTerms}
                  onChange={(e) => setAckTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-amber-800"
                />
                <span>
                  {info.acknowledgments?.photo_terms?.text ||
                    "I understand that photos should show only my pet, with no people in the image, and that my photo may be lightly adjusted for layout, clarity, or background cleanup."}
                </span>
              </label>
              <label className="mt-3 flex items-start gap-3 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  data-testid="ack-photo-rights-checkbox"
                  checked={ackRights}
                  onChange={(e) => setAckRights(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-amber-800"
                />
                <span>
                  {info.acknowledgments?.photo_rights?.text ||
                    "I confirm that I own this photo or have permission to use it for my Paw-Some Report."}
                </span>
              </label>
            </section>

            {/* One-time link notice */}
            <p
              data-testid="one-time-link-notice"
              className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              This is a one-time upload link and will expire after your photos are successfully
              submitted.
            </p>

            {submitError && (
              <p
                data-testid="photo-upload-error"
                className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                {submitError}
              </p>
            )}

            <button
              type="button"
              data-testid="photo-upload-submit"
              disabled={!canSubmit}
              onClick={submit}
              className="mt-4 w-full rounded-md bg-amber-800 py-3 text-base font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:bg-neutral-300 hover:bg-amber-900"
            >
              {submitting ? "Uploading…" : "Upload Photos"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PetSection({ report, files, error, onPick, onRemove }) {
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const count = files.length;
  const withinRange = count >= MIN_PHOTOS && count <= MAX_PHOTOS && !error;

  return (
    <section
      data-testid={`pet-upload-section-${report.report_id}`}
      className="mt-4 rounded-lg bg-white p-4 shadow-sm sm:p-5"
    >
      <h2
        data-testid={`pet-heading-${report.report_id}`}
        className="text-lg font-semibold sm:text-xl"
      >
        Great! Let's Meet {report.pet_name}!
      </h2>
      <p className="mt-1 text-xs text-neutral-500">
        JPG, PNG, WEBP, or HEIC — up to 15 MB each. {MIN_PHOTOS}–{MAX_PHOTOS} photos of{" "}
        {report.pet_name}.
      </p>

      <input
        data-testid={`pet-file-input-${report.report_id}`}
        type="file"
        accept={ACCEPT_EXTS.join(",")}
        multiple
        onChange={onPick}
        className="mt-3 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-amber-800 file:px-4 file:py-2 file:text-white hover:file:bg-amber-900"
      />

      {previews.length > 0 && (
        <ul
          data-testid={`pet-preview-list-${report.report_id}`}
          className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          {previews.map((src, i) => (
            <li
              key={src}
              className="relative overflow-hidden rounded-md bg-neutral-100"
              data-testid={`pet-preview-${report.report_id}-${i}`}
            >
              <img src={src} alt="" className="h-24 w-full object-cover" />
              <button
                type="button"
                data-testid={`pet-remove-${report.report_id}-${i}`}
                onClick={() => onRemove(i)}
                aria-label={`Remove photo ${i + 1}`}
                className="absolute right-1 top-1 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white hover:bg-black/80"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <p
        data-testid={`pet-count-indicator-${report.report_id}`}
        className={`mt-2 text-sm ${withinRange ? "text-emerald-700" : "text-neutral-600"}`}
      >
        {count} of {MIN_PHOTOS}–{MAX_PHOTOS} photos selected for {report.pet_name}
      </p>

      {error && (
        <p
          data-testid={`pet-error-${report.report_id}`}
          className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {error}
        </p>
      )}
    </section>
  );
}

function MissPawSomeTips() {
  return (
    <section
      data-testid="miss-paw-some-tips"
      className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-950"
    >
      <p className="text-sm font-semibold">A few photo tips from Miss Paw-Some 🐾</p>
      <ul className="mt-2 list-disc pl-5 text-sm">
        <li>Clear, close-up photos usually work best.</li>
        <li>Good lighting helps.</li>
        <li>Show off your pet's personality.</li>
        <li>Silly faces, happy smiles, and favorite moments are always welcome.</li>
      </ul>
    </section>
  );
}

function SuccessBlock({ petNames }) {
  const label = joinNames(petNames);
  return (
    <div
      data-testid="upload-success"
      className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-emerald-900"
    >
      <p data-testid="upload-success-heading" className="text-lg font-semibold">
        We received {label}'s photos!
      </p>
      <p className="mt-2 text-sm">
        Thank you. We'll review the photos and let you know if we need anything else.
        Otherwise, {petNames.length > 1 ? "your reports are" : `${label}'s report is`} underway.
      </p>
    </div>
  );
}
